import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  ManageDataDrawerParams,
  DataItem,
  InsightData,
} from './insights-data.interface';
import { DrawerService } from '../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { SvgIconComponent } from 'angular-svg-icon';
import { CommonModule } from '@angular/common';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { InsightsDataService } from './insights-data.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { CommonService } from '../../../../shared/services/common.service';
import { EnterDataComponent } from './enter-data/enter-data.component';
import { ErrorResponse } from '../../../../core/models/error.models';
import { ConfirmDialogComponent } from '../../../../components/confirm-dialog/confirm-dialog.component';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { DataGraphComponent } from './data-graph/data-graph.component';
import { PrintDatasheetComponent } from './print-datasheet/print-datasheet.component';
import { OrganizationService } from '../../../organizations/organization.service';
import { OrganizationModel } from '../../../organizations/organization.interface';
import { CustomTimeService } from '../../../manage-settings/custom-time/custom-time.service';
import { OrgScheduleList } from '../../../manage-settings/manage-settings.interface';
import { instructionData } from './datasheet-instruction';
import { ViewBipDrawerNavigationService } from '../view-bip-case/shared/view-bip-drawer-navigation.service';
import { DrawerHeaderComponent } from '../../../../components/drawer-header/drawer-header.component';
@Component({
  selector: 'app-insights-data',
  standalone: true,
  imports: [
    SvgIconComponent,
    CommonModule,
    NzDividerModule,
    NzTableModule,
    NzButtonModule,
    NzSpinModule,
    ConfirmDialogComponent,
    DrawerHeaderComponent,
  ],
  templateUrl: './insights-data.component.html',
})
export class InsightsDataComponent implements OnDestroy, OnInit {
  @ViewChild('drawerTitleEnterData')
  drawerTitleEnterData!: TemplateRef<void>;
  @ViewChild('drawerTitleDataGraph')
  drawerTitleDataGraph!: TemplateRef<void>;

  caseParams!: ManageDataDrawerParams;
  private readonly destroy$ = new Subject<void>();
  loading$: Observable<boolean> = of(false);
  insightsData: InsightData = {} as InsightData;
  showConfirmDialog = false;
  isDeleting = false;
  selectedUnit = 0;
  isPrintLoading = false;
  constructor(
    private readonly drawerService: DrawerService,
    private readonly insightsService: InsightsDataService,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService,
    private readonly injector: Injector,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly orgService: OrganizationService,
    private readonly customTimeService: CustomTimeService,
    private readonly viewBipDrawerNavigationService: ViewBipDrawerNavigationService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerSubscription();
  }

  listOfData: DataItem[] = [
    {
      name: 'Frequency/Duration Based Datasheet',
      action: '',
      isEnabled: true,
      isPrintLoading: false,
    },
    { name: 'Latency', action: '', isEnabled: false, isPrintLoading: false },
    {
      name: 'Partial Interval',
      action: '',
      isEnabled: false,
      isPrintLoading: false,
    },
    {
      name: 'Replacement Behavior',
      action: '',
      isEnabled: false,
      isPrintLoading: false,
    },
  ];

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageDataDrawerParams>(DRAWER_IDS.MANAGE_DATA)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
        }
      });
  }

  ngOnInit(): void {
    this.getInsightsData();
  }

  private getInsightsData() {
    if (this.caseParams?.behavior) {
      this.loaderService.show();
      this.insightsService
        .getInsightData(this.caseParams?.behavior?.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (response.result) {
              this.insightsData = response.result;
            }

            this.loaderService.hide();
            this.cdr.detectChanges();
            this.listOfData[0].isEnabled =
              this.insightsData.frequencyBasedDatasheet;
            this.listOfData[2].isEnabled = this.insightsData.partialInterval;
            this.listOfData[1].isEnabled = this.insightsData.latency;
            if (this.insightsData.isReplacementStratageyApplied) {
              this.listOfData[3].isEnabled =
                this.insightsData.replacementBehavior;
            } else {
              this.listOfData = this.listOfData.filter(
                (_, index) => index !== 3
              );
              this.cdr.detectChanges();
            }
            this.cdr.detectChanges();
          },
          error: error => {
            this.loaderService.hide();
            this.commonService.notify('error', error.message);
          },
        });
    }
  }

  openEnterDataDrawer(unitNo: number): void {
    this.drawerService.openDrawer(
      EnterDataComponent,
      this.drawerTitleEnterData as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        selectedUnit: unitNo,
        caseDetail: this.caseParams.case,
        behavior: this.caseParams.behavior,
        caseBehaviorId: this.caseParams.behavior?.id,
        selectedUnitName: this.listOfData[unitNo - 1].name,
      },
      DRAWER_IDS.ENTER_DATA
    );
  }

  closeEnterDataDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.ENTER_DATA);
  }

  openDataGraphsDrawer(unitNo: number): void {
    this.drawerService.openDrawer(
      DataGraphComponent,
      this.drawerTitleDataGraph as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        selectedUnit: unitNo,
        caseDetail: this.caseParams.case,
        behavior: this.caseParams.behavior,
        caseBehaviorId: this.caseParams.behavior?.id,
      },
      DRAWER_IDS.DATA_GRAPH
    );
  }

  closeDataGraphDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.DATA_GRAPH);
  }

  enableDataSheet(unitNo: number) {
    if (this.caseParams.caseBehaviorId) {
      this.loaderService.show();
      const operation$ = this.insightsService.enableDatasheet({
        caseBehaviorId: this.caseParams.caseBehaviorId,
        unitName: unitNo,
      });
      operation$
        .pipe(
          take(1),
          takeUntil(this.destroy$),
          finalize(() => {
            this.loaderService.hide();
          })
        )
        .subscribe({
          next: () => {
            this.getInsightsData();
          },
          error: error => {
            this.loaderService.hide();
            this.handleError(error);
          },
        });
    }
  }

  removeDatasheet() {
    if (this.caseParams.caseBehaviorId) {
      this.isDeleting = true;
      const operation$ = this.insightsService.removeDatasheet({
        caseBehaviorId: this.caseParams.caseBehaviorId,
        unitName: this.selectedUnit,
      });
      operation$
        .pipe(
          take(1),
          takeUntil(this.destroy$),
          finalize(() => {
            this.loaderService.hide();
          })
        )
        .subscribe({
          next: () => {
            this.isDeleting = false;
            this.showConfirmDialog = false;
            this.getInsightsData();
            this.commonService.notify(
              'success',
              'Record deleted successfully.'
            );
          },
          error: error => {
            this.isDeleting = false;
            this.showConfirmDialog = false;
            this.handleError(error);
          },
        });
    }
  }
  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  toggleData(isEnable: boolean, unitNo: number) {
    this.selectedUnit = unitNo;
    if (isEnable) {
      this.showConfirmDialog = true;
    } else {
      this.enableDataSheet(unitNo);
    }
  }
  handleCancel() {
    this.showConfirmDialog = false;
    this.isDeleting = false;
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  getDataSheetInfo() {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    return instructionData.datasheets.find(
      (sheet: any) => sheet.unit === this.selectedUnit
    );
  }
  async printDatasheet(index: number, data: DataItem) {
    this.selectedUnit = index;
    const factory = this.viewContainerRef.createComponent(
      PrintDatasheetComponent,
      {
        injector: this.injector,
      }
    );
    const componentElement = factory.location.nativeElement;
    componentElement.style.display = 'none';
    data.isPrintLoading = true;
    if (this.caseParams.case?.student.organizationId) {
      await Promise.all([
        this.orgService
          .getOrganization(this.caseParams?.case?.student.organizationId)
          .toPromise(),
        this.customTimeService
          .getOrgSechduleById(this.caseParams?.case?.student.customScheduleId)
          .toPromise(),
      ])
        .then(
          async ([orgRes, schedules]: [
            OrganizationModel | undefined,
            OrgScheduleList[] | undefined,
          ]) => {
            let base64Image = '';
            if (orgRes?.logoUrl) {
              base64Image = await this.commonService.getBase64ImageFromUrl(
                orgRes.logoUrl
              );
            } else {
              base64Image = await this.commonService.getBase64ImageFromUrl(
                '/assets/images/logo.png'
              );
            }

            factory.instance.dataSheetData = {
              caseDetail: this.caseParams.case,
              logo: base64Image,
              orgDetail: orgRes,
              behavior: this.caseParams.behavior,
              datasheetInfo: this.getDataSheetInfo(),
              timeSlots: schedules ? schedules[0].timeSlots : [],
              behaviorNames:
                this.caseParams.behavior?.behaviorCategoryTypes.map(
                  item => item.behaviorTypeName
                ),
            };
            this.cdr.detectChanges();
            this.waitForInitialization(factory).then(async () => {
              await factory.instance.printPDF();
              factory.destroy();
            });
          }
        )
        .catch(e => console.error(e))
        .finally(() => {
          data.isPrintLoading = false;
          this.cdr.detectChanges();
        });
    }
  }

  waitForInitialization(factory: ComponentRef<PrintDatasheetComponent>) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }
  openDetailsDrawer() {
    const drawer = this.drawerService.getDrawerRef(DRAWER_IDS.MANAGE_DATA);
    drawer?.close({ navigationHandled: true });
  }
}
